import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';

import { useAuthToken, useQuery } from '..';

type Props = {
  folderId: string | null | undefined;
  text: string;
  enabled?: boolean;
};

function normalizeOption(v: string) {
  return v.trim();
}

function uniqCaseInsensitive(values: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach((v) => {
    const key = v.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });
  return out;
}

export function useContentHubAutoCompletion({ folderId, text, enabled = true }: Props) {
  const { setAlert } = useAppContext();
  const token = useAuthToken();

  const trimmed = text.trim();
  const queryLower = trimmed.toLowerCase();
  const isEnabled = Boolean(enabled && token && folderId && trimmed.length >= 2);

  const queryKey = `client-contenthub-auto-completion-${folderId}-${trimmed}`;

  const {
    data = {},
    error,
    isLoading
  } = useQuery({
    queryKey,
    queryData: { folderId, trimmed },
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('folder_id', String(folderId));
      params.set('text', trimmed);

      const url = `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/contenthub/auto-completion?${params.toString()}`;
      return getJson({ url, token }).then(
        (response: TypeAny & ResponseObject & ResponseExpiredToken) => {
          if ((response as ResponseExpiredToken).isExpired) {
            logout();
          }

          if ('code' in response) {
            setAlert(response.code);
          }

          const raw = Array.isArray(response)
            ? response
            : response?.influencer_handles ??
              response?.influencerHandles ??
              response?.handles ??
              response?.suggestions ??
              response?.items ??
              response?.options ??
              [];

          const arr = Array.isArray(raw) ? raw : [];
          const options = uniqCaseInsensitive(
            arr
              .filter((x) => typeof x === 'string')
              .map((x) => normalizeOption(x as string))
              .filter(Boolean)
          )
            .filter((v) => v.toLowerCase().includes(queryLower))
            .sort((a, b) => {
              const aLower = a.toLowerCase();
              const bLower = b.toLowerCase();
              const aStarts = aLower.startsWith(queryLower);
              const bStarts = bLower.startsWith(queryLower);
              if (aStarts !== bStarts) return aStarts ? -1 : 1;
              return a.localeCompare(b);
            });

          return { options };
        }
      );
    },
    enabled: isEnabled,
    staleTime: 30_000,
    refetchOnMount: false
  });

  return {
    isLoading,
    error,
    options: (data as { options: string[] }).options ?? []
  };
}
